import { z } from 'zod';
import { lenientString, lenientUuid, lenientDateString, lenientOptionalString, lenientEnum, lenientRequiredNode, lenientNodeArray } from '../schema-extensions';
import { VehicleType } from '../enums/vehicle-type.enum';
import { ValueHistoryNode } from './value-history-node-schema';
import { OdometerReadingNode } from './odometer-reading-node-schema';

// ============================================================================
// CONSTANTS (must be defined before schemas that reference them)
// ============================================================================
const VEHICLE_CONSTANTS = {
  REGISTRATION_MAX_LENGTH: 20,
  MAKE_MAX_LENGTH: 50,
  MODEL_MAX_LENGTH: 50,
  YEAR_MAX_LENGTH: 4,
  COLOR_MAX_LENGTH: 30,
  DATE_FORMAT_LENGTH: 10, // 'YYYY-MM-DD'
  TYRE_TYPE_MAX_LENGTH: 50,
  NOTES_MAX_LENGTH: 1000,
};

// ============================================================================
// SCHEMAS
// ============================================================================

/**
 * Lenient schema for vehicle - accepts various formats and coerces to standard
 */
const lenientVehicleNodeSchema = z.object({
  id: lenientUuid(),
  registration: lenientString(VEHICLE_CONSTANTS.REGISTRATION_MAX_LENGTH),
  type: lenientEnum(VehicleType, VehicleType.CAR),
  make: lenientString(VEHICLE_CONSTANTS.MAKE_MAX_LENGTH),
  model: lenientString(VEHICLE_CONSTANTS.MODEL_MAX_LENGTH),
  year: lenientOptionalString(VEHICLE_CONSTANTS.YEAR_MAX_LENGTH).optional(),
  color: lenientOptionalString(VEHICLE_CONSTANTS.COLOR_MAX_LENGTH).optional(),
  motDueDate: lenientDateString('').optional(),
  taxDueDate: lenientDateString('').optional(),
  value: lenientRequiredNode(ValueHistoryNode),
  drivers: z.array(z.string()),
  tyreType: lenientOptionalString(VEHICLE_CONSTANTS.TYRE_TYPE_MAX_LENGTH).optional(),
  odometer: lenientNodeArray(OdometerReadingNode.deserialize),
  notes: lenientString(VEHICLE_CONSTANTS.NOTES_MAX_LENGTH),
}).transform(val => ({
  id: val.id,
  registration: val.registration,
  type: val.type,
  make: val.make,
  model: val.model,
  year: val.year,
  color: val.color,
  motDueDate: val.motDueDate,
  taxDueDate: val.taxDueDate,
  value: val.value,
  drivers: val.drivers,
  tyreType: val.tyreType,
  odometer: val.odometer,
  notes: val.notes
}));

/**
 * Strict schema for vehicle - enforces exact format
 */
const strictVehicleNodeSchema = z.object({
  id: z.string().min(1),
  registration: z.string().max(VEHICLE_CONSTANTS.REGISTRATION_MAX_LENGTH),
  type: z.nativeEnum(VehicleType),
  make: z.string().max(VEHICLE_CONSTANTS.MAKE_MAX_LENGTH),
  model: z.string().max(VEHICLE_CONSTANTS.MODEL_MAX_LENGTH),
  year: z.string().max(VEHICLE_CONSTANTS.YEAR_MAX_LENGTH).optional(),
  color: z.string().max(VEHICLE_CONSTANTS.COLOR_MAX_LENGTH).optional(),
  motDueDate: z.string().length(VEHICLE_CONSTANTS.DATE_FORMAT_LENGTH).optional(),
  taxDueDate: z.string().length(VEHICLE_CONSTANTS.DATE_FORMAT_LENGTH).optional(),
  value: z.unknown().transform(val => ValueHistoryNode.deserialize(val)),
  drivers: z.array(z.string()),
  tyreType: z.string().max(VEHICLE_CONSTANTS.TYRE_TYPE_MAX_LENGTH).optional(),
  odometer: z.array(z.unknown().transform(val => OdometerReadingNode.deserialize(val))),
  notes: z.string().max(VEHICLE_CONSTANTS.NOTES_MAX_LENGTH),
}).transform(val => ({
  id: val.id,
  registration: val.registration,
  type: val.type,
  make: val.make,
  model: val.model,
  year: val.year,
  color: val.color,
  motDueDate: val.motDueDate,
  taxDueDate: val.taxDueDate,
  value: val.value,
  drivers: val.drivers,
  tyreType: val.tyreType,
  odometer: val.odometer,
  notes: val.notes
}));

// ============================================================================
// INTERFACE
// ============================================================================

export interface VehicleNode {
  id: string;
  registration: string;
  type: VehicleType;
  make: string;
  model: string;
  year?: string;
  color?: string;
  motDueDate?: string;
  taxDueDate?: string;
  value: ValueHistoryNode;
  drivers: string[];
  tyreType?: string;
  odometer: OdometerReadingNode[];
  notes: string;
}

// ============================================================================
// NAMESPACE WITH METHODS
// ============================================================================

export namespace VehicleNode {
  /**
   * Safely deserialize unknown data into VehicleNode
   * Uses lenient parsing to handle various input formats
   */
  export function deserialize(data: unknown): VehicleNode {
    return lenientVehicleNodeSchema.parse(data);
  }

  /**
   * Create a new VehicleNode with validation
   * Uses strict parsing to ensure data integrity
   */
  export function create(data: VehicleNode): VehicleNode {
    return strictVehicleNodeSchema.parse(data);
  }

  /**
   * Validate that data conforms to VehicleNode structure
   */
  export function validate(data: unknown): data is VehicleNode {
    return strictVehicleNodeSchema.safeParse(data).success;
  }

  /**
   * Get a display name for the vehicle
   */
  export function getDisplayName(vehicle: VehicleNode): string {
    const parts = [vehicle.make, vehicle.model];
    if (vehicle.year) parts.push(`(${vehicle.year})`);
    return parts.join(' ');
  }

  /**
   * Get the current value of the vehicle
   */
  export function getCurrentValue(vehicle: VehicleNode): number | undefined {
    return ValueHistoryNode.getCurrentValue(vehicle.value);
  }

  /**
   * Check if the vehicle has been sold
   */
  export function isSold(vehicle: VehicleNode): boolean {
    return ValueHistoryNode.isSold(vehicle.value);
  }

  /**
   * Get the latest odometer reading
   */
  export function getLatestOdometerReading(vehicle: VehicleNode): number | undefined {
    if (vehicle.odometer.length === 0) return undefined;
    const sorted = [...vehicle.odometer].sort((a, b) => b.date.localeCompare(a.date));
    return sorted[0].reading;
  }

  export function isActive(vehicle: VehicleNode): boolean {
    return !VehicleNode.isSold(vehicle);
  }

  /**
   * Constants for validation and UI
   */
  export const Constants = VEHICLE_CONSTANTS;
}
